import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getCountries, getCountryCallingCode, parsePhoneNumber, } from 'libphonenumber-js';
import 'flag-icons/css/flag-icons.min.css';
import styles from './PhoneInput.module.css';
var DEFAULT_COUNTRY = 'FR';
var CALLING_CODE_TO_COUNTRY = (function () {
    var map = {};
    for (var _i = 0, _a = getCountries(); _i < _a.length; _i++) {
        var c = _a[_i];
        var code = getCountryCallingCode(c);
        if (!map[code])
            map[code] = c;
    }
    return map;
})();
function getCountryFromValue(value) {
    if (!value || value === '+')
        return DEFAULT_COUNTRY;
    try {
        var parsed = parsePhoneNumber(value);
        if (parsed === null || parsed === void 0 ? void 0 : parsed.country)
            return parsed.country;
    }
    catch (_a) {
        /* fall through */
    }
    var digits = value.replace(/\D/g, '');
    if (digits.length > 0) {
        var exact = CALLING_CODE_TO_COUNTRY[digits];
        if (exact)
            return exact;
        var codes = Object.keys(CALLING_CODE_TO_COUNTRY).sort(function (a, b) { return b.length - a.length; });
        for (var _i = 0, codes_1 = codes; _i < codes_1.length; _i++) {
            var code = codes_1[_i];
            if (digits.startsWith(code))
                return CALLING_CODE_TO_COUNTRY[code];
        }
    }
    return DEFAULT_COUNTRY;
}
/** Lowercase, strip diacritics — so "Réunion" matches a "reunion" search. */
function normalize(text) {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}
/** Build the country list with localized names and calling codes, sorted by name. */
function buildCountryOptions(language) {
    var regionNames;
    try {
        regionNames = new Intl.DisplayNames([language], { type: 'region' });
    }
    catch (_a) {
        regionNames = undefined;
    }
    var options = getCountries().map(function (code) {
        var _a;
        var name = (_a = regionNames === null || regionNames === void 0 ? void 0 : regionNames.of(code)) !== null && _a !== void 0 ? _a : code;
        var callingCode = getCountryCallingCode(code);
        return {
            code: code,
            name: name,
            callingCode: callingCode,
            search: normalize("".concat(name, " ").concat(code, " +").concat(callingCode)),
        };
    });
    options.sort(function (a, b) { return a.name.localeCompare(b.name, language, { sensitivity: 'base' }); });
    return options;
}
export default function PhoneInput(_a) {
    var _b;
    var value = _a.value, onChange = _a.onChange, onBlur = _a.onBlur, onCountryChange = _a.onCountryChange, error = _a.error, label = _a.label, _c = _a.disabled, disabled = _c === void 0 ? false : _c, id = _a.id, name = _a.name;
    var _d = useTranslation(), t = _d.t, i18n = _d.i18n;
    var _e = useState(function () { return getCountryFromValue(value); }), country = _e[0], setCountry = _e[1];
    var countryOptions = useMemo(function () { return buildCountryOptions(i18n.language); }, [i18n.language]);
    // --- country dropdown state ---
    var _f = useState(false), open = _f[0], setOpen = _f[1];
    var _g = useState(''), query = _g[0], setQuery = _g[1];
    var _h = useState(0), activeIndex = _h[0], setActiveIndex = _h[1];
    var rootRef = useRef(null);
    var searchRef = useRef(null);
    var listRef = useRef(null);
    var filteredOptions = useMemo(function () {
        var q = normalize(query.trim().replace(/^\+/, ''));
        if (!q)
            return countryOptions;
        return countryOptions.filter(function (o) { return o.search.includes(q); });
    }, [countryOptions, query]);
    var selectedOption = useMemo(function () { return countryOptions.find(function (o) { return o.code === country; }); }, [countryOptions, country]);
    useEffect(function () {
        setCountry(function (prev) {
            var next = getCountryFromValue(value);
            if (next !== prev) {
                onCountryChange === null || onCountryChange === void 0 ? void 0 : onCountryChange(next);
                return next;
            }
            return prev;
        });
    }, [value, onCountryChange]);
    var closeDropdown = useCallback(function () {
        setOpen(false);
        setQuery('');
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
    }, [onBlur]);
    // close on outside click
    useEffect(function () {
        if (!open)
            return;
        var handlePointer = function (e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                closeDropdown();
            }
        };
        document.addEventListener('mousedown', handlePointer);
        return function () { return document.removeEventListener('mousedown', handlePointer); };
    }, [open, closeDropdown]);
    // when opening, focus the search box and reset the active row to the selection
    useEffect(function () {
        var _a;
        if (open) {
            (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            var idx = filteredOptions.findIndex(function (o) { return o.code === country; });
            setActiveIndex(idx >= 0 ? idx : 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // keep the highlighted row scrolled into view
    useEffect(function () {
        if (!open || !listRef.current)
            return;
        var el = listRef.current.children[activeIndex];
        el === null || el === void 0 ? void 0 : el.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, open]);
    var prefix = '+' + getCountryCallingCode(country);
    var selectCountry = useCallback(function (newCountry) {
        setCountry(newCountry);
        onCountryChange === null || onCountryChange === void 0 ? void 0 : onCountryChange(newCountry);
        onChange('+' + getCountryCallingCode(newCountry));
        closeDropdown();
    }, [onChange, onCountryChange, closeDropdown]);
    var handleSearchKeyDown = useCallback(function (e) {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(function (i) { return Math.min(i + 1, filteredOptions.length - 1); });
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(function (i) { return Math.max(i - 1, 0); });
                break;
            case 'Enter': {
                e.preventDefault();
                var opt = filteredOptions[activeIndex];
                if (opt)
                    selectCountry(opt.code);
                break;
            }
            case 'Escape':
                e.preventDefault();
                closeDropdown();
                break;
            default:
                break;
        }
    }, [filteredOptions, activeIndex, selectCountry, closeDropdown]);
    var handleInputChange = useCallback(function (e) {
        var national = e.target.value.replace(/\D/g, '');
        onChange(national ? prefix + national : prefix);
    }, [prefix, onChange]);
    var displayValue = useMemo(function () {
        if (!value || value === prefix)
            return '';
        if (value.startsWith(prefix))
            return value.slice(prefix.length);
        try {
            var parsed = parsePhoneNumber(value);
            if (parsed && parsed.country === country)
                return parsed.nationalNumber;
        }
        catch (_a) {
            /* ignore */
        }
        return value.replace(/\D/g, '');
    }, [value, country, prefix]);
    var inputId = (_b = id !== null && id !== void 0 ? id : name) !== null && _b !== void 0 ? _b : 'phone-input';
    var errorId = inputId + '-error';
    var listboxId = inputId + '-country-list';
    return (_jsxs("div", { className: styles.root, children: [label && (_jsx("label", { htmlFor: inputId, children: label })), _jsxs("div", { className: styles.row, children: [_jsxs("div", { className: styles.country, ref: rootRef, children: [_jsxs("button", { type: "button", className: styles.countryTrigger, onClick: function () { return (open ? closeDropdown() : setOpen(true)); }, disabled: disabled, "aria-haspopup": "listbox", "aria-expanded": open, "aria-label": t('form.phone'), children: [_jsx("span", { className: "fi fi-".concat(country.toLowerCase(), " ").concat(styles.flag), "aria-hidden": "true" }), _jsxs("span", { className: styles.dialCode, children: ["+", getCountryCallingCode(country)] }), _jsx("span", { className: styles.caret, "aria-hidden": "true" })] }), open && (_jsxs("div", { className: styles.panel, children: [_jsx("input", { ref: searchRef, type: "text", className: styles.search, value: query, onChange: function (e) {
                                            setQuery(e.target.value);
                                            setActiveIndex(0);
                                        }, onKeyDown: handleSearchKeyDown, placeholder: t('form.country_search'), role: "combobox", "aria-controls": listboxId, "aria-expanded": open, "aria-autocomplete": "list", "aria-activedescendant": filteredOptions[activeIndex]
                                            ? "".concat(listboxId, "-").concat(filteredOptions[activeIndex].code)
                                            : undefined }), _jsxs("ul", { ref: listRef, id: listboxId, className: styles.list, role: "listbox", children: [filteredOptions.length === 0 && (_jsx("li", { className: styles.empty, children: t('form.country_none') })), filteredOptions.map(function (opt, index) { return (_jsxs("li", { id: "".concat(listboxId, "-").concat(opt.code), role: "option", "aria-selected": opt.code === country, className: styles.option +
                                                    (index === activeIndex ? ' ' + styles.optionActive : '') +
                                                    (opt.code === country ? ' ' + styles.optionSelected : ''), onMouseEnter: function () { return setActiveIndex(index); }, onMouseDown: function (e) {
                                                    // prevent the search input losing focus before we handle the click
                                                    e.preventDefault();
                                                    selectCountry(opt.code);
                                                }, children: [_jsx("span", { className: "fi fi-".concat(opt.code.toLowerCase(), " ").concat(styles.flag), "aria-hidden": "true" }), _jsx("span", { className: styles.optionName, children: opt.name }), _jsxs("span", { className: styles.optionCode, children: ["+", opt.callingCode] })] }, opt.code)); })] })] }))] }), _jsx("input", { id: inputId, name: name, type: "tel", inputMode: "numeric", autoComplete: "tel-national", className: styles.numberInput, value: displayValue, onChange: handleInputChange, onBlur: onBlur, disabled: disabled, placeholder: selectedOption ? 'e.g. 612345678' : '', "aria-invalid": !!error, "aria-describedby": error ? errorId : undefined })] }), error && (_jsx("span", { id: errorId, className: "error", role: "alert", children: error }))] }));
}
